function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-31 22:57:43 +0100 (Sat, 31 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160223 $
//  Purpose   : templating part base class
// =============================================================================
// ---Third-party imports
import React, { Component, Fragment, useContext } from 'react';
import { Dimmer, Loader } from 'semantic-ui-react';
import ExtendableError from 'es6-error';
import levenshtein from 'fast-levenshtein';
import memoizeOne from 'memoize-one';
import clsx from 'clsx';
// pretty costly - 10kb gzipped and only useful without terser run (dev-mode)
//~ let StackTracey = null
//~ import StackTracey from 'stacktracey'
// ---Framework imports
import { iterItems, size, keys, cloneDeep, has, includes, dict, isString, entries, sorted, isEqual, isFunction } from 'ldash';
import { Parts } from 'uix/registry';
import { Actions } from 'uix/actions';
import log from 'uix/logging';
import { createPortal } from 'uix/components';
import { TracebackDiv, ErrorBoundary, errorHandler } from 'uix/components/error';
import { partProperties, Sel } from 'uix/registries';
import { cssToReactStyle, ucFirst } from 'uix/utils';
import { selectDomainNamedParts, getRunId } from 'uix/selectors';
import connect, { DEFAULT_ACTION_MAP } from 'uix/connect';
import { Graphics } from 'uix/graphics';
import { getUtilityChunk, isUtilityChunkLoaded, chunkNames } from 'uix/chunks';
import { hasGroup } from 'uix/auth';
import { PartContext, usePartContext, useSelector, useEffect, useState } from 'uix/hooks';
// ---Globals
/**
 * Create react elements from part definition list
 *
 * @parts {Array} of 2-tuples, Part constructors with config
 * @return {Array} created react elements.
 */ const getPartName = (props)=>props.name || props.type;
export const createPart = (part, cfg, key)=>{
    /* create part and inject some props */ let { type, name, ...partcfg } = cfg;
    let props = {
        type,
        name,
        partcfg,
        key
    };
    // partProperties are read from funcPart spec or from static Part members during part registration
    let partProps = partProperties[type];
    if (!partProps) {
        // this should not happen, the caller chould ensure it's a valid part type
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                "Bad Part: ",
                type
            ]
        }, type);
    }
    // also accessible as part.WrappedComponent.getGraphics
    // but injecting here into props seems to be more sane
    if (size(partProps.getGraphics) > 0) {
        props.Graphics = dict(partProps.getGraphics.map((name)=>[
                name,
                Graphics[name]
            ]));
    }
    if (size(partProps.getChunkedUtils) > 0) {
        let unloadedChunks = partProps.getChunkedUtils.filter((name)=>!isUtilityChunkLoaded(name));
        if (unloadedChunks.length > 0) {
            return 'Unloaded chunks';
        }
        let chunkedUtils = dict(partProps.getChunkedUtils.map((name)=>[
                name,
                getUtilityChunk(name)
            ]));
        props = {
            ...props,
            ...chunkedUtils
        };
    }
    return /*#__PURE__*/ _jsx(ErrorBoundary, {
        name: getPartName(cfg),
        fallbackProps: {
            className: `part ${type}`
        },
        children: /*#__PURE__*/ React.createElement(part, props)
    }, key);
};
export const createParts = (parts)=>{
    // parts is a list of [(React.Component, cfg), ..]
    if (parts.length == 0) {
        return null;
    }
    return parts.map(([part, cfg], index)=>createPart(part, cfg, `${getPartName(cfg)}_${index}`));
};
const defaultDefaultActionMap = {
    ...DEFAULT_ACTION_MAP,
    reloadPartFilters: Actions.reloadPartFilters
};
// XXX: should be in sync with usePartContext
export const selectDefaultProps = (root, partFilterData)=>{
    let data = {};
    let errors = {};
    iterItems(partFilterData, (filterName, f)=>{
        // collect root elements from filter output and combine in data
        data = {
            ...data,
            ...f.meta.error ? {} : f.data
        };
        // separate full filter if error
        if (f.meta.error) {
            errors[filterName] = f.meta.error;
        }
    });
    if (size(errors) > 0) {
        data['errors'] = errors;
    }
    let request = root.meta.request;
    let config = root.config.ddata[request.dname];
    return {
        // it's important to use only stable references from store
        // or we will trigger re-renderings on every store update
        ...data,
        // see PartContext partProps
        dname: request.dname,
        odid: config.id,
        request: root.meta.request,
        // user context
        user: Sel.user(root),
        // config of request domain
        dconfig: config.dconfig,
        types: config.types,
        // selected i18n language context
        language: root.meta.language,
        // i18n data for language/domain, use .get(IDENT) or .span(IDENT)
        i18n: Sel.i18n(root),
        // instance config
        iconfig: root.config.instance,
        // raw domain data for request dname
        ddata: Sel.domain(root),
        // all domains of instance, only basic info
        domains: root.config.domains,
        // helper for building fully qualified urls for dev/prod-mode compatibility
        // by adding FQDN prefixes to urls in dev-mode
        apiurl: Sel.apiurl(root)
    };
};
export const selectPartData = (root, props)=>{
    let { partcfg, addUnbound = false } = props;
    let { storeKey } = partcfg;
    if (storeKey === undefined) {
        // default "part-of-tab" case
        storeKey = getPartName(props);
        addUnbound = true;
    }
    let partFilterData = root.data.parts[storeKey];
    if (addUnbound && root.data.parts.Unbound) {
        // unbound data keys are overwritten by bound ones
        partFilterData = {
            ...root.data.parts.Unbound,
            ...partFilterData
        };
    }
    return selectDefaultProps(root, partFilterData);
};
const defaultStoreMap = (root, props)=>selectPartData(root, props);
const renderPartWrap = (props, children, partStyle, className, boundFuncs, wrapInDiv)=>{
    /* render outer part div and call _render()
    also handle render exceptions (do this really? what about error boundaries?)
    and filter-api-errors that are ariving as props.errors
    */ let partName = getPartName(props);
    let { type, request, partcfg, errors } = props;
    let style = cloneDeep(partStyle) || {};
    // add some css props from templating config
    if (partcfg.style) {
        let pstyle = isString(partcfg.style) ? cssToReactStyle(partcfg.style) : partcfg.style;
        style = {
            ...style,
            ...pstyle
        };
    }
    //~ if (errors) {
    //~ log.error('filter api errors', errors)
    // ther was once a renderer here
    //~ }
    try {
        let { i18n, dconfig, domains, dname, ddata, types, apiurl, user, iconfig } = props;
        let partProps = {
            i18n,
            dconfig,
            iconfig,
            domains,
            dname,
            ddata,
            user,
            types,
            apiurl,
            request
        };
        let chunks = dict(chunkNames.map((cn)=>[
                cn,
                props[cn]
            ]).filter((e)=>e[1]));
        // keep everything stable here - boundFuncs
        let ctx = {
            name: partName,
            partcfg,
            reload: boundFuncs.reload,
            ...partProps,
            // we push into props, so we push directly into context namespace
            ...chunks
        };
        let content = isFunction(children) ? /*#__PURE__*/ React.createElement(children, props) : children;
        return /*#__PURE__*/ _jsx(PartContext.Provider, {
            value: ctx,
            children: wrapInDiv ? /*#__PURE__*/ _jsx("div", {
                className: clsx('part', type, partcfg.cssClass, className),
                name: partName,
                style: style,
                children: content
            }) : content
        });
    } catch (e) {
        log.exception(e);
        throw e;
    }
};
export const funcPart = (descr, renderChildren)=>{
    /* functional part form, give a description and/or a render function
   */ if (renderChildren === undefined) {
        renderChildren = descr;
        descr = {};
    }
    // give this anonymous function a react name
    renderChildren.displayName = 'FuncPart';
    // XXX: this memo is probably not needed
    let reloadMemo = memoizeOne((partKey, reloadPartFilters)=>()=>reloadPartFilters(partKey));
    let render_f = (props)=>{
        let boundFuncs = {
            reload: reloadMemo(props.partcfg.storeKey || getPartName(props), props.reloadPartFilters)
        };
        return renderPartWrap(props, renderChildren, descr.partStyle, descr.className, boundFuncs, descr.wrapped !== false // render wrapping div
        );
    };
    // return description that will be connected in part registry
    return {
        // the render function
        __render_f: render_f,
        // default description elements
        storeMap: defaultStoreMap,
        defaultActionMap: defaultDefaultActionMap,
        // overwrite other defaults above
        ...descr
    };
};
class _StandalonePart extends Component {
    static getDerivedStateFromProps(props, state) {
        let { loadStandalonePart, dname, runId, name, depsKey, params } = props;
        let reloadParams = {
            name,
            dname,
            params,
            depsKey
        };
        if (!isEqual(state.reloadParams, reloadParams)) {
            let partCfg = _StandalonePart.getPartCfg(props);
            if (partCfg) {
                let cfg = {
                    ...partCfg,
                    ...params
                };
                loadStandalonePart(name, _StandalonePart.storeKey(props), dname, cfg, runId);
            }
            return {
                name,
                params,
                reloadParams
            };
        }
        return null;
    }
    static getPartCfg(props) {
        let { name, type, namedParts, partCfg } = props;
        // construct a valid partcfg
        let res = {
            name,
            type,
            filters: []
        };
        if (namedParts[name]) {
            res = namedParts[name];
        }
        res = {
            ...res,
            ...partCfg
        };
        return res;
    }
    renderUnknown(name, available) {
        let proposals = sorted(available.map((e)=>[
                levenshtein.get(e, name),
                e
            ]), (e)=>e[0]);
        let filtered = proposals.filter((e)=>e[0] < 5).map((e)=>e[1]);
        let propose = null;
        if (filtered.length > 0) {
            propose = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("br", {}),
                    'Did you mean: ',
                    filtered.map((e, i)=>/*#__PURE__*/ _jsxs("tt", {
                            children: [
                                " ",
                                e
                            ]
                        }, i)),
                    '?'
                ]
            });
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                'Unknown <part>:',
                /*#__PURE__*/ _jsx("b", {
                    children: /*#__PURE__*/ _jsxs("tt", {
                        children: [
                            " ",
                            name
                        ]
                    })
                }),
                propose
            ]
        });
    }
    render() {
        let { namedParts, filterData, params, partLoaded } = this.props;
        let storeKey = _StandalonePart.storeKey(this.props);
        let { name } = this.state;
        let partcfg = _StandalonePart.getPartCfg(this.props);
        if (!partcfg) {
            // not registered as named part in template config
            // or no type prop given
            return this.renderUnknown(name, keys(namedParts));
        }
        if (!Parts.get(partcfg.type)) {
            // not implemented/registered in uix
            return /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx("tt", {
                    children: partcfg.type
                })
            });
        }
        if (!partLoaded) {
            return null;
        }
        let part = Parts.get(partcfg.type);
        return createPart(part, {
            ...partcfg,
            ...params,
            storeKey
        });
    }
    constructor(props){
        super(props);
        this.state = {
            name: null,
            reloadParams: {}
        };
    }
}
/* controlled loading of embedded parts
  we wait for chunk availablity
  but not for filter-data availability
  */ _define_property(_StandalonePart, "storeMap", (root, props)=>({
        namedParts: selectDomainNamedParts(root),
        dname: root.meta.request.dname,
        runId: getRunId(root),
        filterData: root.data.parts[_StandalonePart.storeKey(props)],
        partLoaded: Boolean(root.meta.standaloneParts[_StandalonePart.storeKey(props)])
    }));
_define_property(_StandalonePart, "defaultProps", {
    storeKey: 0,
    depsKey: null,
    partCfg: {}
});
_define_property(_StandalonePart, "actionMap", {
    loadStandalonePart: Actions.loadStandalonePart
});
_define_property(_StandalonePart, "storeKey", ({ name, storeKey })=>`${name}_${storeKey || 0}`);
export const StandalonePart = connect('StandalonePart', _StandalonePart);
export const UnknownPart = funcPart(({ user, partcfg })=>{
    // Render Name of missing Part an a proposal if levenshtein distance < 5
    if (!user.hasGroup('debug')) {
        // deactivate name output
        return null;
    }
    let { unknownType } = partcfg;
    let similarities = keys(Parts.components).map((e)=>[
            levenshtein.get(e, unknownType),
            e
        ]);
    let sorted = similarities.sort((a, b)=>a[0] - b[0]);
    //~ log(sorted)
    let filtered = sorted.filter((e)=>e[0] < 5).map((e)=>e[1]);
    //~ log(filtered)
    let proposal = '';
    if (filtered.length > 0) {
        proposal = `[${filtered[0]}?]`;
    }
    return /*#__PURE__*/ _jsx("div", {
        children: `Part: ${unknownType} ${proposal}`
    });
});
